// Which clock button (if any) the Attendance card should show.
//
// In-office attendance is NOT one punch a day: people step out and come back,
// so they clock in and out in PAIRS all through office hours. What closes for
// the day is the ability to START a new punch, and only at the shift end —
// closing an already-open punch must always stay possible, otherwise someone
// who works till 18:15 on an 18:00 shift is stuck clocked in.
//
// Overtime re-opens Clock In after the shift end (and Clock Out after that),
// until the overtime cutoff. Remote employees have no shift bound at all.

export type ClockAction = "clock-out" | "clock-in" | "closed" | "loading";

export interface ClockButtonInput {
  /** Live session state: more clock-ins than clock-outs today. null = not yet fetched. */
  clockedIn: boolean | null;
  /** Now is past the employee's office end time. False when no shift end is configured. */
  shiftEnded: boolean;
  /** Remote, or overtime currently active — neither is bound by the shift window. */
  unlimited: boolean;
}

export const resolveClockAction = ({
  clockedIn,
  shiftEnded,
  unlimited,
}: ClockButtonInput): ClockAction => {
  // A punch that is already open can always be closed — including after the
  // shift ended, which is the whole point of the rule.
  if (clockedIn) return "clock-out";
  if (clockedIn === null) return "loading";
  if (unlimited || !shiftEnded) return "clock-in";
  return "closed";
};

/** True when `now` is past "HH:MM" on the same local day. No bound → never. */
export const isPastShiftEnd = (officeEnd: string | null | undefined, now: number): boolean => {
  if (!officeEnd) return false;
  const [hh, mm] = officeEnd.split(":").map(Number);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return false;
  const end = new Date(now);
  end.setHours(hh, mm, 0, 0);
  return now > end.getTime();
};
